import { FC, useState, useEffect, useMemo } from "react";
import styles from "./PropertyViews.module.scss";
import useTranslationHook from "hooks/useTranslationHook";
import useCountry from "hooks/useCountry";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import Image from "@Components/Image";
import wrapperHOC from "@Utils/wrapperHOC";

interface PropertyViewsProps {
  metaGeneralFacts: any[];
  metaCommunityFacts: any;
}

const PropertyViews: FC<PropertyViewsProps> = ({
  metaGeneralFacts = [],
  metaCommunityFacts = {},
}) => {
  const { t } = useTranslationHook();
  const country = useCountry("IN");
  const [activeIdx, setActiveIdx] = useState(0);

  const facts = useMemo(() => {
    let metaFactsTemp: any[] = [];

    // 1. Community Facts Priority (from MetaFacts.tsx)
    if (country === "IN") {
      const country_meta_facts = (metaCommunityFacts as any)?.[country] ?? [];
      const localFacts: any[] = [];
      country_meta_facts?.forEach((cmf: any) => {
        if (cmf?.name) {
          let value: any = null;
          switch (cmf.name) {
            case "bookings_in_90days":
              value = (
                <>
                  <span className={styles.boldText}>{cmf.count} students</span> booked this property
                  recently
                </>
              );
              break;
            case "enquires_in_30days":
              value = (
                <>
                  <span className={styles.boldText}>{cmf.count} students</span> enquired for this
                  property recently
                </>
              );
              break;
            case "previous_year_booking":
              value = (
                <>
                  <span className={styles.boldText}>{cmf.count} students</span> booked this property
                  last year
                </>
              );
              break;
            default:
              break;
          }
          if (value) localFacts.push({ ...cmf, displayValue: value });
        }
      });

      const nameArray = localFacts.map((f) => f.name);
      if (nameArray.includes("bookings_in_90days")) {
        metaFactsTemp = localFacts.filter((f) => f.name === "bookings_in_90days");
      } else if (nameArray.includes("enquires_in_30days")) {
        metaFactsTemp = localFacts.filter((f) => f.name === "enquires_in_30days");
      } else if (nameArray.includes("previous_year_booking")) {
        metaFactsTemp = localFacts.filter((f) => f.name === "previous_year_booking");
      }
    }

    // 2. General Facts fallback
    const sortedMetaGeneralFacts = [...metaGeneralFacts].sort(
      (a: any, b: any) => b.count - a.count,
    );
    const recentIndex = sortedMetaGeneralFacts.findIndex(
      (obj: any) => obj.name === "recent_booking",
    );
    if (recentIndex > -1) {
      const [obj] = sortedMetaGeneralFacts.splice(recentIndex, 1);
      sortedMetaGeneralFacts.unshift(obj);
    }

    if (
      metaFactsTemp.length > 0 &&
      (metaFactsTemp[0].name === "bookings_in_90days" ||
        metaFactsTemp[0].name === "previous_year_booking")
    ) {
      sortedMetaGeneralFacts.forEach((smgf: any) => {
        if (metaFactsTemp.length >= 2) return;
        if (smgf.name !== "recent_booking") metaFactsTemp.push(smgf);
      });
    } else if (metaFactsTemp.length > 0 && metaFactsTemp[0].name === "enquires_in_30days") {
      sortedMetaGeneralFacts.forEach((smgf: any) => {
        if (metaFactsTemp.length >= 2) return;
        if (smgf.name !== "enquires_in_15days") metaFactsTemp.push(smgf);
      });
    } else {
      metaFactsTemp = [...metaFactsTemp, ...sortedMetaGeneralFacts].slice(0, 2);
    }

    return metaFactsTemp.map((f) => {
      if (f.displayValue) return f;
      // If no displayValue yet, create one
      const val = f.value;
      if (val && typeof val === "string") {
        // Parse the HTML string and wrap numbers followed by "students" in bold tags
        const boldifiedVal = val.replace(
          /(\d+)\s+(students?)/gi,
          `<span class="${styles.boldText}">$1 $2</span>`,
        );
        return { ...f, displayValue: <span dangerouslySetInnerHTML={{ __html: boldifiedVal }} /> };
      }

      // Fallback formatting
      let disc = f.name?.replace(/_/g, " ") || "students activity";
      if (f.name === "shortlisted") disc = "shortlisted this property in last 30 days";
      if (f.name?.includes("view")) disc = "viewed this property in last 24 hours";

      return {
        ...f,
        displayValue: (
          <>
            <span className={styles.boldText}>{f.count} students</span> {disc}
          </>
        ),
      };
    });
  }, [metaGeneralFacts, metaCommunityFacts, country, t]);

  useEffect(() => {
    let interval: any;
    if (facts.length > 1) {
      interval = setInterval(() => {
        setActiveIdx((prev) => (prev + 1) % facts.length);
      }, 4000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [facts.length]);

  const currentFact = facts[activeIdx];
  if (!currentFact) return null;

  const avatars = [
    "https://static-assets.amberstudent.com/images/Avatars/PPAR1.svg",
    "https://static-assets.amberstudent.com/images/Avatars/PPAR2.svg",
    activeIdx % 2 === 0
      ? "https://static-assets.amberstudent.com/images/Avatars/PPAR3.svg"
      : "https://static-assets.amberstudent.com/images/Avatars/PPAR4.svg",
  ];

  return (
    <div className={formatClassNames(styles.container, styles.slideIn)} key={activeIdx}>
      <div className={styles.avatarContainer}>
        {avatars.map((src, index) => (
          <div key={index} className={styles.avatar}>
            <Image src={src} alt="User" width={44} height={44} />
            {index === 2 && activeIdx % 2 === 0 && <div className={styles.onlineDot} />}
            {index === 2 && activeIdx % 2 === 1 && (
              <div className={styles.heartIcon}>
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 14 14"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M7 12.25L6.125 11.4625C3.5 9.1 1.75 7.5125 1.75 5.5625C1.75 4.0625 2.9375 2.875 4.4375 2.875C5.2625 2.875 6.0625 3.25 6.5625 3.8375C7.0625 3.25 7.8625 2.875 8.6875 2.875C10.1875 2.875 11.375 4.0625 11.375 5.5625C11.375 7.5125 9.625 9.1 7 11.4625L6.125 12.25Z"
                    fill="#EF4444"
                  />
                </svg>
              </div>
            )}
          </div>
        ))}
      </div>
      <div className={styles.text}>{currentFact.displayValue}</div>
    </div>
  );
};

PropertyViews.displayName = "PropertyViews";

export default wrapperHOC(PropertyViews, {
  componentName: "PropertyViews-PropertyDetailsSection-PropertyPage",
  showForChina: true,
});
